// ContextualHelpSystem.js - Module d'aide contextuelle séparé
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  HelpCircle, 
  X, 
  ArrowRight, 
  CheckCircle2,
  Info,
  Target,
  Compass,
  Zap,
  Search,
  BookOpen,
  Eye,
  Sparkles
} from 'lucide-react';

// ============================================================================
// DONNÉES DE CONTENU D'AIDE
// ============================================================================

export const HELP_CONTENT = {
  ambition: {
    title: "Votre objectif",
    description: "Formulez ce que vous voulez accomplir avec l'aide de Aladinn",
    tips: [
      "Aladinn vous posera 3 questions simples et progressives",
      "Répondez naturellement, sans vous censurer", 
      "Soyez précis sur le résultat que vous visez",
      "Mentionnez un délai réaliste"
    ],
    nextAction: "Aladinn créera automatiquement vos recommandations",
    icon: Target,
    color: "purple"
  },
  strategy: {
    title: "Stratégie",
    description: "Aladinn analyse votre objectif et propose une stratégie personnalisée",
    tips: [
      "Lisez attentivement la démarche proposée et les recommandations",
      "Identifiez vos priorités d'action"
    ],
    nextAction: "Un plan d'action détaillé sera créé",
    icon: Compass,
    color: "blue"
  },
  action: {
    title: "Plan d'action",
    description: "Aladinn vous propose des étapes clés",
    tips: [
      "Vérifier que ces étapes sont claires et adaptées",
      "Ajustez les délais à votre ryhtme ",

    ],
    nextAction: "Trouvez l'inspiration",
    icon: Zap,
    color: "orange"
  },
  inspiration: {
    title: "Inspiration",
    description: "Trouver des ressources pertinentes",
    tips: [
      "Aladinn précise automatiquement votre objectif pour une recherche ciblée",
      "Sources mondiales non sponsorisées de haute qualité",
      "Recherche et analyse dans plus de 20 langues",
      "Classification automatique par fiabilité et pertinence"
    ],
    nextAction: "Constituer votre base de connaissances",
    icon: Search,
    color: "green"
  },
  exploration: {
    title: "Exploration",
    description: "Créez votre base de connaissance ",
    tips: [
      "Constitution progressive adaptée à vos objectifs",
      "Moteur de recherche vectorielle pour des résultats pertinents",
      "Explication détaillée par concept",
      "Quiz interactifs contextualisés à vos objectifs"
    ],
    nextAction: "Utilisation continue pour enrichir votre projet",
    icon: BookOpen,
    color: "teal"
  }
};

// ============================================================================
// COMPOSANT TOOLTIP
// ============================================================================

export const HelpTooltip = ({ children, content, position = 'bottom', isVisible, onClose }) => {
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const updatePosition = () => {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        
        const tooltipWidth = 230; // Réduit de 256px à 230px (10% supplémentaire)
        const tooltipHeight = 200; // Hauteur estimée
        const gap = 12; // Espace entre le bouton et le tooltip
        
        const spaceBelow = windowHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;

        let top, left;
        let newPosition = position;
        
        // Déterminer si on affiche en haut ou en bas
        if (position === 'bottom' && spaceBelow < tooltipHeight && spaceAbove > spaceBelow) {
          newPosition = 'top';
        } else if (position === 'top' && spaceAbove < tooltipHeight && spaceBelow > spaceAbove) {
          newPosition = 'bottom';
        }

        // Calculer la position horizontale (centré sur le bouton)
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2);
        
        // Ajuster si déborde à gauche ou à droite
        if (left < 10) {
          left = 10;
        } else if (left + tooltipWidth > windowWidth - 10) {
          left = windowWidth - tooltipWidth - 10;
        }

        // Calculer la position verticale
        if (newPosition === 'top' || newPosition.includes('top')) {
          top = triggerRect.top - tooltipHeight - gap;
        } else {
          top = triggerRect.bottom + gap;
        }

        setTooltipPosition(newPosition);
        setTooltipStyle({
          position: 'fixed',
          top: `${Math.max(10, top)}px`,
          left: `${left}px`,
          maxWidth: '14.375rem', // 230px en rem (230/16)
          zIndex: 9999
        });
      };

      updatePosition();
      
      // Mettre à jour la position lors du scroll ou resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible, position]);

  if (!isVisible) {
    return <div ref={triggerRef} className="inline-block">{children}</div>;
  }

  // Créer le contenu du tooltip
  const tooltipContent = (
    <div style={tooltipStyle}>
      <div className="bg-white border border-gray-200 rounded-lg shadow-2xl p-4">
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-semibold text-gray-800 text-sm">{content.title}</h4>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors ml-2"
            >
              <X className="w-3 h-3 text-gray-500" />
            </button>
          )}
        </div>
        <div className="text-xs text-gray-600 space-y-2">
          {content.description && (
            <p className="font-medium text-purple-700">{content.description}</p>
          )}
          {content.tips && (
            <ul className="space-y-1">
              {content.tips.slice(0, 3).map((tip, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle2 className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}
          {content.nextAction && (
            <div className="flex items-center text-blue-600 font-medium pt-1">
              <ArrowRight className="w-3 h-3 mr-2" />
              <span>{content.nextAction}</span>
            </div>
          )}
        </div>
      </div>
      {/* Overlay transparent pour fermer en cliquant à l'extérieur */}
      <div 
        className="fixed inset-0 z-[-1]"
        onClick={onClose}
        style={{ zIndex: 9998 }}
      />
    </div>
  );

  return (
    <>
      <div ref={triggerRef} className="inline-block">
        {children}
      </div>
      {/* Utiliser un portail pour rendre le tooltip à la racine du document */}
      {ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
};

// ============================================================================
// HOOK D'AIDE CONTEXTUELLE
// ============================================================================

export const useContextualHelp = (stepId) => {
  const [showHelp, setShowHelp] = useState(false);
  const [hasSeenHelp, setHasSeenHelp] = useState(false);

  useEffect(() => {
    // Marquer comme vu quand l'aide est affichée
    if (showHelp) {
      setHasSeenHelp(true);
      const viewedSteps = JSON.parse(localStorage.getItem('aladinn_viewed_help') || '[]');
      if (!viewedSteps.includes(stepId)) {
        viewedSteps.push(stepId);
        localStorage.setItem('aladinn_viewed_help', JSON.stringify(viewedSteps));
      }
    }
  }, [showHelp, stepId]);

  const toggleHelp = () => setShowHelp(!showHelp);
  const hideHelp = () => setShowHelp(false);
  const helpContent = HELP_CONTENT[stepId];

  return { 
    showHelp, 
    toggleHelp, 
    hideHelp, 
    helpContent, 
    hasSeenHelp 
  };
};

// ============================================================================
// COMPOSANT BOUTON D'AIDE
// ============================================================================

export const HelpButton = ({ 
  stepId, 
  isActive = false, 
  size = 'sm',
  variant = 'icon',
  className = "",
  title = "Aide contextuelle"
}) => {
  const { showHelp, toggleHelp, hideHelp, helpContent, hasSeenHelp } = useContextualHelp(stepId);

  if (!helpContent) return null;

  const handleClose = () => {
    hideHelp();
  };

  const sizeClasses = {
    'xs': 'w-3 h-3',
    'sm': 'w-4 h-4', 
    'md': 'w-5 h-5',
    'lg': 'w-6 h-6'
  };

  const buttonSizeClasses = {
    'xs': 'p-0.5',
    'sm': 'p-1',
    'md': 'p-2', 
    'lg': 'p-2.5'
  };

  if (variant === 'text') {
    return (
      <HelpTooltip
        content={helpContent}
        isVisible={showHelp}
        onClose={handleClose}
      >
        <button
          onClick={toggleHelp}
          className={`flex items-center space-x-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium ${className}`}
          title={title}
        >
          <HelpCircle className="w-4 h-4" />
          <span>Aide</span>
        </button>
      </HelpTooltip>
    );
  }

  return (
    <HelpTooltip
      content={helpContent}
      isVisible={showHelp}
      onClose={handleClose}
    >
      <button
        onClick={toggleHelp}
        className={`relative ${buttonSizeClasses[size]} rounded-full transition-all ${
          isActive 
            ? 'bg-purple-100 text-purple-600 animate-pulse' 
            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
        } ${className}`}
        title={title}
      >
        <HelpCircle className={sizeClasses[size]} />
        {isActive && !hasSeenHelp && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full animate-ping" />
        )}
      </button>
    </HelpTooltip>
  );
};

// ============================================================================
// COMPOSANT BANDEAU D'ACCUEIL
// ============================================================================

export const WelcomeBanner = ({ onClose, onShowGuide }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('aladinn_welcome_seen', 'true');
    onClose?.();
  };

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('aladinn_welcome_seen');
    if (hasSeenWelcome) {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-200 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-800">Bienvenue sur Aladinn !</h3>
            <p className="text-sm text-purple-700">Découvrez votre parcours de transformation personnalisé</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onShowGuide}
            className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Guide</span>
          </button>
          <button
            onClick={handleClose}
            className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL DE TOUR GUIDÉ
// ============================================================================

export const GuidedTour = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Bienvenue sur Aladinn !</h3>
              <p className="text-sm text-gray-600">Découvrez votre parcours personnalisé</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="font-medium text-purple-800 mb-2">Votre parcours en 5 étapes</h4>
            <div className="space-y-2 text-sm text-purple-700">
              <div className="flex items-center">
                <Target className="w-4 h-4 mr-2" />
                <span>1. Ambition - Définissez votre objectif</span>
              </div>
              <div className="flex items-center">
                <Compass className="w-4 h-4 mr-2" />
                <span>2. Stratégie - Recommandations IA</span>
              </div>
              <div className="flex items-center">
                <Zap className="w-4 h-4 mr-2" />
                <span>3. Action - Plan personnalisé</span>
              </div>
              <div className="flex items-center">
                <Search className="w-4 h-4 mr-2" />
                <span>4. Inspiration - Recherche 360°</span>
              </div>
              <div className="flex items-center">
                <BookOpen className="w-4 h-4 mr-2" />
                <span>5. Exploration - Base de connaissances</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800 mb-1">Aide contextuelle</h4>
                <p className="text-sm text-blue-700">
                  Cliquez sur les icônes <HelpCircle className="w-3 h-3 inline mx-1" /> 
                  pour obtenir de l'aide ciblée à chaque étape
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-6">
          <span className="text-xs text-gray-500">Cette aide reste disponible à tout moment</span>
          <button
            onClick={onClose}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            Commencer
          </button>
        </div>
      </div>
    </div>
  );
};

// Export par défaut pour la compatibilité
const ContextualGuideSystem = { 
  HelpTooltip,
  HelpButton,
  WelcomeBanner,
  GuidedTour,
  useContextualHelp,
  HELP_CONTENT
};

export default ContextualGuideSystem;